import { memo } from 'react';

import { cssClasses, cssVarList } from 'pkg/css/utils';
import type { CalendarDayType } from 'pkg/datetime';
import DateTime from 'pkg/datetime';

import {
	dayCursor,
	dayForeground,
	dayHighlight,
	dayHoverBg,
	dayHoverFg,
	dayWeight,
} from 'components/form/calendar/constants';
import {
	DAY_STYLES_CLASSNAMES,
	MARKED_DATE_DEFAULT_COLOR,
	type MarkedDateGroup,
} from 'components/form/calendar/types';

import * as css from './styles.css';

interface CalendarDayProps {
	calendarDay: CalendarDayType;
	index: number;
	range: boolean;
	multiple: boolean;
	selectable: boolean;
	selectedDates: Date[];
	highlightedDates: Date[][];
	markedDates: MarkedDateGroup[];
	isSelectable: boolean;
	emitDateChange: (date: Date) => void;
}

export const CalendarDay = memo(
	({
		calendarDay,
		index,
		range,
		multiple,
		selectable,
		selectedDates,
		highlightedDates,
		markedDates,
		isSelectable,
		emitDateChange,
	}: CalendarDayProps) => {
		const { day, timestamp, isCurrentMonth, isToday } = calendarDay;
		const dateTime = new DateTime(new Date(timestamp));

		const localSelectable = isSelectable && isCurrentMonth;

		const isSelected = selectedDates.some((d) => dateTime.sameDay(+d));

		const markColors = markedDates
			.filter((group) => group.dates.some((d) => dateTime.sameDay(+d)))
			.map((group) => group.color ?? MARKED_DATE_DEFAULT_COLOR);

		const isWithinAppliedRange = highlightedDates.some((da) =>
			dateTime.isBetween(+da[0], +da[1])
		);

		const appliedRangeStart = highlightedDates.some((da) =>
			dateTime.sameDay(+da[0])
		);

		const appliedRangeEnd = highlightedDates.some((da) =>
			dateTime.sameDay(+da[1])
		);

		let isInSelectedRange = false;

		const isStartOfRange =
			range &&
			selectedDates[1] !== undefined &&
			dateTime.sameDay(+selectedDates[0]);

		const isEndOfRange = range && dateTime.sameDay(+selectedDates[1]);

		let calendarDayState: DAY_STYLES_CLASSNAMES = DAY_STYLES_CLASSNAMES.idle;

		if (!localSelectable) {
			calendarDayState = DAY_STYLES_CLASSNAMES.disabled;
		}

		// @NOTE If calendar is marked as selectable={false} just mark is as idle.
		if (!selectable && isCurrentMonth) {
			calendarDayState = DAY_STYLES_CLASSNAMES.idle;

			// @NOTE Make sure today is still marked as active.
			if (isToday) {
				calendarDayState = DAY_STYLES_CLASSNAMES.active;
			}
		}

		if (!isCurrentMonth) {
			calendarDayState = DAY_STYLES_CLASSNAMES.outsideRange;
		}

		// In multiple mode a selected day from an adjacent month keeps the muted
		// outside-range look: it isn't clickable here, so styling it as active
		// would advertise an interaction that does nothing. It stays selected in
		// its own month. Range mode is exempt — its endpoints must stay visible
		// alongside the shaded days between them, which are not month-scoped, or a
		// range crossing a month boundary renders with washed-out ends.
		if (isSelected && (isCurrentMonth || !multiple)) {
			calendarDayState = DAY_STYLES_CLASSNAMES.active;
		}

		// In multiple mode every selected date stands alone, so days between two
		// of them must not be shaded as an inferred range.
		if (
			!isSelected &&
			!multiple &&
			selectedDates.length === 2 &&
			dateTime.isBetween(+selectedDates[0], +selectedDates[1])
		) {
			calendarDayState = DAY_STYLES_CLASSNAMES.insideSelectedRange;
			isInSelectedRange = true;
		}

		const onMouseDown = () => {
			if (localSelectable) {
				emitDateChange(dateTime.toDate());
			}
		};

		const showMarkedDots =
			markColors.length > 0 &&
			isCurrentMonth &&
			!isSelected &&
			!isInSelectedRange;

		return (
			<time
				key={index}
				onMouseDown={onMouseDown}
				style={cssVarList({
					'day-highlight': dayHighlight[calendarDayState],
					'day-foreground': dayForeground[calendarDayState],
					'day-cursor': dayCursor[calendarDayState],
					'day-weight': dayWeight[calendarDayState],
					'day-hover-bg': dayHoverBg[calendarDayState],
					'day-hover-fg': dayHoverFg[calendarDayState],
				})}
				className={cssClasses(
					css.day,
					!isCurrentMonth ? css.outsideRange : '',
					isToday ? css.isToday : '',
					isStartOfRange ? css.startOfRange : '',
					isEndOfRange ? css.endOfRange : '',
					isInSelectedRange ? css.insideSelectedRange : '',
					isWithinAppliedRange ? css.withinAppliedRange : '',
					appliedRangeStart ? css.startAppliedRange : '',
					appliedRangeEnd ? css.endAppliedRange : ''
				)}>
				{day}
				{showMarkedDots && (
					<span className={css.markedDots}>
						{markColors.map((color, colorIndex) => (
							<span
								key={colorIndex}
								className={css.markedDot}
								style={cssVarList({ 'dot-color': color })}
							/>
						))}
					</span>
				)}
			</time>
		);
	}
);
